import { useCallback, useEffect, useState } from 'react';
import { ApiError, integrations as api } from '@/helpers/ApiHelper';
import type { IntegrationStatus } from '@/types/integrations';

const POLL_MS = 30_000;

export function useIntegrations() {
  const [rows, setRows] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setRows(await api.list());
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const setEnabled = useCallback(async (adapter: string, enabled: boolean) => {
    try {
      const updated = await api.update(adapter, { enabled });
      setRows((prev) => prev.map((r) => (r.adapter === adapter ? updated : r)));
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Request failed');
      throw e;
    }
  }, []);

  const stop = useCallback(async (adapter: string) => {
    try {
      const res = await api.stop(adapter);
      setRows((prev) => prev.map((r) => (r.adapter === adapter ? res.status : r)));
      return res.dropped;
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Request failed');
      throw e;
    }
  }, []);

  return { rows, loading, error, refresh, setEnabled, stop };
}
